import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { catchError, of, Subject, takeUntil } from 'rxjs';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { WishlistHeartComponent } from '../wishlist/wishlist-heart/wishlist-heart.component';
import {
  SearchService,
  SearchResult,
  SearchListing,
  SearchFilters,
  SubcategoriesResponse,
} from '../services/search.service';
import {
  ReverseImageSearchService,
  ReverseImageSearchResult,
} from '../services/reverse-image-search.service';
import { WishlistService } from '../services/wishlist.service';
import { AuthService } from '../services/auth.service';

interface ReverseImageSearchData {
  type: 'reverse-image-search';
  searchImageUrl: string;
  results: ReverseImageSearchResult[];
  params: any;
  timestamp: number;
}

interface LocalListing extends SearchListing {
  currentImageIndex: number;
  displayImage: string;
  preloadedImages?: Set<number>;
  isImageLoading?: boolean;
  touchStartX?: number;
  touchStartY?: number;
  touchStartTime?: number;
  showCarouselControls?: boolean;
  controlsTimeout?: any;
  hasShownControls?: boolean;
  isTouchScrolling?: boolean;
  touchMoved?: boolean;
  lastTouchX?: number;
  lastTouchY?: number;
}

interface LocalReverseResult extends ReverseImageSearchResult {
  currentImageIndex: number;
  displayImage: string;
  preloadedImages?: Set<number>;
  isImageLoading?: boolean;
  touchStartX?: number;
  touchStartY?: number;
  touchStartTime?: number;
  showCarouselControls?: boolean;
  controlsTimeout?: any;
  hasShownControls?: boolean;
  isTouchScrolling?: boolean;
  touchMoved?: boolean;
  lastTouchX?: number;
  lastTouchY?: number;
}

@Component({
  selector: 'app-search',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    HeaderComponent,
    FooterComponent,
    WishlistHeartComponent,
  ],
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.css'],
})
export class SearchComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  currentKeyword: string = '';
  currentCategory: string = 'all';
  currentSubcategory?: string;
  currentSort: string = 'relevance';
  currentPage: number = 1;
  currentFilters: SearchFilters = {};
  searchResult?: SearchResult;
  isLoading: boolean = false;
  error?: string;
  availableSubcategories: Array<{ key: string; label: string }> = [];
  isLoadingSubcategories: boolean = false;
  isChangingSubcategory: boolean = false;
  pendingSubcategory?: string;
  resultsVisible: boolean = true;
  showFilters: boolean = false;
  Math = Math;
  isReverseImageSearch: boolean = false;
  reverseSearchData?: ReverseImageSearchData;
  reverseSearchResults: LocalReverseResult[] = [];
  searchImageUrl?: string;
  loadedImages: Set<string> = new Set();
  processedListings: LocalListing[] = [];

  private readonly SWIPE_THRESHOLD = 30;
  private readonly SWIPE_FAST_THRESHOLD = 20;
  private readonly SWIPE_VELOCITY_THRESHOLD = 0.3;
  private readonly SWIPE_TIME_THRESHOLD = 600;
  private readonly VERTICAL_SCROLL_THRESHOLD = 25;
  private readonly TAP_MOVEMENT_THRESHOLD = 10;
  private readonly TAP_TIME_THRESHOLD = 300;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private searchService: SearchService,
    private reverseImageSearchService: ReverseImageSearchService,
    private wishlistService: WishlistService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.route.url.pipe(takeUntil(this.destroy$)).subscribe((segments) => {
      const path = segments.map((s) => s.path).join('/');

      if (path === 'search/reverse-image-results') {
        this.handleReverseImageSearchResults();
      } else {
        this.isReverseImageSearch = false;
        this.handleRegularSearch();
      }
    });

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((queryParams) => {
        if (!this.isReverseImageSearch) {
          this.handleQueryParams(queryParams);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupExpiredReverseSearchData();
  }

  private cleanupExpiredReverseSearchData(): void {
    const data = sessionStorage.getItem('reverseImageSearchResults');
    if (data) {
      try {
        const reverseSearchData = JSON.parse(data) as ReverseImageSearchData;
        const now = Date.now();
        const dataAge = now - reverseSearchData.timestamp;

        if (dataAge > 5 * 60 * 1000) {
          sessionStorage.removeItem('reverseImageSearchResults');
        }
      } catch (error) {
        sessionStorage.removeItem('reverseImageSearchResults');
      }
    }
  }

  private handleReverseImageSearchResults(): void {
    this.isReverseImageSearch = true;
    this.isLoading = true;

    const data = sessionStorage.getItem('reverseImageSearchResults');
    if (!data) {
      this.error = 'No reverse image search results found';
      this.isLoading = false;
      return;
    }

    try {
      this.reverseSearchData = JSON.parse(data) as ReverseImageSearchData;

      const now = Date.now();
      const dataAge = now - this.reverseSearchData.timestamp;
      if (dataAge > 5 * 60 * 1000) {
        this.error =
          'Search results have expired. Please perform a new search.';
        this.isLoading = false;
        sessionStorage.removeItem('reverseImageSearchResults');
        return;
      }

      this.reverseSearchResults = this.reverseSearchData.results.map((result) =>
        this.convertReverseResultToLocal(result)
      );
      this.searchImageUrl = this.reverseSearchData.searchImageUrl;
      this.currentCategory = this.reverseSearchData.params.category || 'all';
      this.currentSort = 'similarity';
      this.sortReverseSearchResults();
      this.isLoading = false;
      this.resultsVisible = true;

      this.loadWishlistStatusForReverseSearch();
    } catch (error) {
      this.error = 'Failed to load search results';
      this.isLoading = false;
      console.error('Error parsing reverse search data:', error);
    }
  }

  private handleRegularSearch(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.handleRouteParams(params);
    });
  }

  private handleRouteParams(params: any): void {
    this.currentKeyword = '';
    this.currentCategory = 'all';
    this.currentSubcategory = undefined;
    this.error = undefined;

    const category = params['category'];
    const subcategory = params['subcategory'];
    const keyword = params['keyword'];

    const validCategories = [
      'all',
      'vehicles',
      'real-estate',
      'marketplace',
      'jobs',
    ];

    if (!category && !subcategory && !keyword) {
      this.currentCategory = 'all';
      this.currentKeyword = '';
    } else if (category && !subcategory && !keyword) {
      if (validCategories.includes(category)) {
        this.currentCategory = category;
        this.currentKeyword = '';
      } else {
        this.error = `Invalid category: ${category}`;
        return;
      }
    } else if (category === 'all' && !subcategory && keyword) {
      this.currentCategory = 'all';
      this.currentKeyword = this.decodeKeyword(keyword);
    } else if (
      category &&
      !subcategory &&
      keyword &&
      validCategories.includes(category)
    ) {
      this.currentCategory = category;
      this.currentKeyword = this.decodeKeyword(keyword);
    } else if (
      category &&
      subcategory &&
      keyword &&
      validCategories.includes(category)
    ) {
      this.currentCategory = category;
      this.currentSubcategory = subcategory;
      this.currentKeyword = this.decodeKeyword(keyword);
    } else {
      this.error = 'Invalid search route';
      console.error('Invalid route params:', {
        category,
        subcategory,
        keyword,
      });
      return;
    }

    if (this.currentCategory !== 'all') {
      this.loadSubcategories();
    } else {
      this.availableSubcategories = [];
    }

    this.performSearch();
  }

  private decodeKeyword(keyword: string): string {
    if (!keyword) return '';

    if (keyword === '*' || keyword === 'browse') {
      return '';
    }

    try {
      return decodeURIComponent(keyword);
    } catch (e) {
      console.warn('Failed to decode keyword:', keyword);
      return keyword;
    }
  }

  private handleQueryParams(queryParams: any): void {
    this.currentPage = parseInt(queryParams['page']) || 1;
    this.currentSort = queryParams['sort'] || 'relevance';

    const newFilters: SearchFilters = {};
    Object.keys(queryParams).forEach((key) => {
      if (!['page', 'sort'].includes(key)) {
        const value = queryParams[key];
        if (value === 'true') {
          newFilters[key] = true;
        } else if (value === 'false') {
          newFilters[key] = false;
        } else if (!isNaN(value) && value !== '') {
          if (
            key.includes('_min') ||
            key.includes('_max') ||
            key.includes('price') ||
            key.includes('year')
          ) {
            newFilters[key] = parseFloat(value);
          } else {
            newFilters[key] = value;
          }
        } else if (value && value.trim()) {
          newFilters[key] = value.trim();
        }
      }
    });
    this.currentFilters = newFilters;
  }

  private performSearch(): void {
    this.isLoading = true;
    this.error = undefined;

    const hasKeyword =
      this.currentKeyword && this.currentKeyword.trim().length > 0;

    this.searchService
      .search(
        hasKeyword ? this.currentKeyword : '',
        this.currentCategory,
        this.currentSubcategory,
        this.currentFilters,
        this.currentPage,
        20,
        this.currentSort
      )
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          this.error = 'Failed to load search results. Please try again later.';
          this.isLoading = false;
          console.error('Search error:', error);
          return of(null);
        })
      )
      .subscribe((result) => {
        if (result) {
          this.searchResult = result;
          this.processedListings = result.listings.map((listing) =>
            this.convertSearchListingToLocal(listing)
          );
          this.isLoading = false;
          this.resultsVisible = true;
          this.isChangingSubcategory = false;
          this.pendingSubcategory = undefined;

          this.loadWishlistStatus();
        }
      });
  }

  private convertSearchListingToLocal(listing: SearchListing): LocalListing {
    const images = listing.images || [];
    const firstImage =
      images.length > 0
        ? images[0]
        : listing.first_image || this.getDefaultImage(listing.category);

    return {
      ...listing,
      images: images.length > 0 ? images : [firstImage],
      currentImageIndex: 0,
      displayImage: firstImage,
      preloadedImages: new Set([0]),
      isImageLoading: false,
      isTouchScrolling: false,
      touchMoved: false,
    };
  }

  private convertReverseResultToLocal(
    result: ReverseImageSearchResult
  ): LocalReverseResult {
    const images = result.images || [];
    const firstImage =
      images.length > 0
        ? images[0]
        : result.first_image || this.getDefaultImage(result.category);

    return {
      ...result,
      images: images.length > 0 ? images : [firstImage],
      currentImageIndex: 0,
      displayImage: firstImage,
      preloadedImages: new Set([0]),
      isImageLoading: false,
      showCarouselControls: false,
      hasShownControls: false,
      isTouchScrolling: false,
      touchMoved: false,
    };
  }

  private isWishlistButtonTouch(event: TouchEvent): boolean {
    const target = event.target as HTMLElement;
    return !!(
      target.closest('.listing-bookmark') ||
      target.closest('app-wishlist-heart')
    );
  }

  onTouchStart(
    event: TouchEvent,
    listing: LocalListing | LocalReverseResult
  ): void {
    if (this.isWishlistButtonTouch(event)) {
      return;
    }

    const touch = event.touches[0];
    listing.touchStartX = touch.clientX;
    listing.touchStartY = touch.clientY;
    listing.lastTouchX = touch.clientX;
    listing.lastTouchY = touch.clientY;
    listing.touchStartTime = Date.now();
    listing.isTouchScrolling = false;
    listing.touchMoved = false;
  }

  onTouchMove(
    event: TouchEvent,
    listing: LocalListing | LocalReverseResult
  ): void {
    if (this.isWishlistButtonTouch(event)) {
      return;
    }

    if (
      listing.touchStartX === undefined ||
      listing.touchStartY === undefined
    ) {
      return;
    }

    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - listing.touchStartX);
    const deltaY = Math.abs(touch.clientY - listing.touchStartY);

    if (
      deltaY > this.TAP_MOVEMENT_THRESHOLD ||
      deltaX > this.TAP_MOVEMENT_THRESHOLD
    ) {
      listing.touchMoved = true;
    }

    if (deltaY > deltaX && deltaY > this.VERTICAL_SCROLL_THRESHOLD) {
      listing.isTouchScrolling = true;
    } else if (deltaX > deltaY && deltaX > 5 && listing.images.length > 1) {
      event.preventDefault();
    }

    listing.lastTouchX = touch.clientX;
    listing.lastTouchY = touch.clientY;
  }

  onTouchEnd(
    event: TouchEvent,
    listing: LocalListing | LocalReverseResult
  ): void {
    if (this.isWishlistButtonTouch(event)) {
      this.resetTouchState(listing);
      return;
    }

    if (!listing.images || listing.images.length <= 1) {
      if (!listing.isTouchScrolling && !listing.touchMoved) {
        this.viewListing(listing);
      }
      this.resetTouchState(listing);
      return;
    }

    if (
      listing.touchStartX === undefined ||
      listing.touchStartY === undefined ||
      listing.touchStartTime === undefined
    ) {
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - listing.touchStartX;
    const deltaY = touch.clientY - listing.touchStartY;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    const deltaTime = Date.now() - listing.touchStartTime;

    if (
      listing.isTouchScrolling ||
      (absDeltaY > this.VERTICAL_SCROLL_THRESHOLD && absDeltaY > absDeltaX)
    ) {
      this.resetTouchState(listing);
      return;
    }

    const velocity = deltaTime > 0 ? absDeltaX / deltaTime : 0;
    const isHorizontalSwipe = absDeltaX > absDeltaY;
    const isQuickEnough = deltaTime < this.SWIPE_TIME_THRESHOLD;
    const isFastSwipe =
      velocity >= this.SWIPE_VELOCITY_THRESHOLD &&
      absDeltaX >= this.SWIPE_FAST_THRESHOLD;
    const isLongEnough = absDeltaX > this.SWIPE_THRESHOLD || isFastSwipe;
    const isNotTooVertical = absDeltaY < this.VERTICAL_SCROLL_THRESHOLD;

    if (
      isHorizontalSwipe &&
      isQuickEnough &&
      isLongEnough &&
      isNotTooVertical
    ) {
      event.stopPropagation();
      event.preventDefault();
      if (deltaX > 0) {
        this.previousImage(listing, event);
      } else {
        this.nextImage(listing, event);
      }
    } else if (
      !listing.touchMoved ||
      (absDeltaX <= this.TAP_MOVEMENT_THRESHOLD &&
        absDeltaY <= this.TAP_MOVEMENT_THRESHOLD &&
        deltaTime <= this.TAP_TIME_THRESHOLD)
    ) {
      this.viewListing(listing);
    }
    this.resetTouchState(listing);
  }

  private resetTouchState(listing: LocalListing | LocalReverseResult): void {
    setTimeout(() => {
      listing.touchStartX = undefined;
      listing.touchStartY = undefined;
      listing.touchStartTime = undefined;
      listing.lastTouchX = undefined;
      listing.lastTouchY = undefined;
      listing.isTouchScrolling = false;
      listing.touchMoved = false;
    }, 100);
  }

  onImageZoneClick(
    event: Event,
    listing: LocalListing | LocalReverseResult,
    zone: 'prev' | 'next' | 'view'
  ): void {
    event.stopPropagation();

    if (zone === 'view') {
      this.viewListing(listing);
    } else if (zone === 'prev') {
      this.previousImage(listing, event);
    } else if (zone === 'next') {
      this.nextImage(listing, event);
    }
  }

  previousImage(
    listing: LocalListing | LocalReverseResult,
    event: Event
  ): void {
    event.stopPropagation();

    if (!listing.images || listing.images.length <= 1) return;

    const newIndex =
      (listing.currentImageIndex - 1 + listing.images.length) %
      listing.images.length;

    this.navigateToImage(listing, newIndex);
  }

  nextImage(listing: LocalListing | LocalReverseResult, event: Event): void {
    event.stopPropagation();

    if (!listing.images || listing.images.length <= 1) return;

    const newIndex = (listing.currentImageIndex + 1) % listing.images.length;

    this.navigateToImage(listing, newIndex);
  }

  private navigateToImage(
    listing: LocalListing | LocalReverseResult,
    targetIndex: number
  ): void {
    listing.isImageLoading = true;
    listing.currentImageIndex = targetIndex;
    listing.displayImage = listing.images[targetIndex];

    if (!listing.preloadedImages) {
      listing.preloadedImages = new Set();
    }
    listing.preloadedImages.add(targetIndex);

    this.preloadAdjacentImages(listing, targetIndex);
  }

  private preloadAdjacentImages(
    listing: LocalListing | LocalReverseResult,
    currentIndex: number
  ): void {
    if (!listing.images || listing.images.length <= 1) return;

    const nextIndex = (currentIndex + 1) % listing.images.length;
    const prevIndex =
      (currentIndex - 1 + listing.images.length) % listing.images.length;

    if (!listing.preloadedImages?.has(nextIndex)) {
      this.preloadImage(listing.images[nextIndex]);
      listing.preloadedImages?.add(nextIndex);
    }

    if (!listing.preloadedImages?.has(prevIndex)) {
      this.preloadImage(listing.images[prevIndex]);
      listing.preloadedImages?.add(prevIndex);
    }
  }

  private preloadImage(url: string): void {
    if (this.loadedImages.has(url)) return;

    const img = new Image();
    img.src = url;
    img.onload = () => {
      this.loadedImages.add(url);
    };
    img.onerror = () => {
      console.warn(`Failed to preload image: ${url}`);
    };
  }

  onListingHover(listing: LocalListing | LocalReverseResult): void {
    if (!listing.images || listing.images.length <= 1) return;

    listing.images.forEach((imageUrl, index) => {
      if (!listing.preloadedImages?.has(index)) {
        this.preloadImage(imageUrl);
        listing.preloadedImages?.add(index);
      }
    });

    if (!listing.hasShownControls) {
      listing.showCarouselControls = true;
      listing.hasShownControls = true;

      if (listing.controlsTimeout) {
        clearTimeout(listing.controlsTimeout);
      }

      listing.controlsTimeout = setTimeout(() => {
        listing.showCarouselControls = false;
      }, 2000);
    }
  }

  onListingLeave(listing: LocalListing | LocalReverseResult): void {
    if (listing.controlsTimeout) {
      clearTimeout(listing.controlsTimeout);
    }
    listing.hasShownControls = false;
    listing.showCarouselControls = false;
  }

  hasMultipleImages(listing: LocalListing | LocalReverseResult): boolean {
    return listing.images && listing.images.length > 1;
  }

  getImageCounter(listing: LocalListing | LocalReverseResult): string {
    return `${listing.currentImageIndex + 1}/${listing.images.length}`;
  }

  onImageLoad(
    imageUrl: string,
    listing?: LocalListing | LocalReverseResult
  ): void {
    this.loadedImages.add(imageUrl);
    if (listing) {
      listing.isImageLoading = false;
    }
  }

  isImageLoaded(imageUrl: string): boolean {
    return this.loadedImages.has(imageUrl);
  }

  private loadWishlistStatus(): void {
    if (
      !this.authService.isLoggedIn() ||
      !this.searchResult ||
      !this.searchResult.listings.length
    ) {
      return;
    }

    const listingsForWishlist = this.searchResult.listings
      .map((listing) => ({
        id: listing.id.toString(),
        table_name: listing.table_name,
      }))
      .filter((item) => item.table_name);

    if (listingsForWishlist.length === 0) {
      return;
    }

    this.wishlistService
      .checkWishlistStatus(listingsForWishlist)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.warn('Failed to load wishlist status:', error);
          return of({});
        })
      )
      .subscribe();
  }

  private loadWishlistStatusForReverseSearch(): void {
    if (!this.authService.isLoggedIn() || !this.reverseSearchResults.length) {
      return;
    }

    const listingsForWishlist = this.reverseSearchResults
      .map((result) => ({
        id: result.id.toString(),
        table_name: result.table_name,
      }))
      .filter((item) => item.table_name);

    if (listingsForWishlist.length === 0) {
      return;
    }

    this.wishlistService
      .checkWishlistStatus(listingsForWishlist)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.warn('Failed to load wishlist status:', error);
          return of({});
        })
      )
      .subscribe();
  }

  private convertReverseResultToSearchListing(
    result: ReverseImageSearchResult
  ): SearchListing {
    return {
      id: result.id,
      title: result.title,
      description: result.description,
      price: result.price,
      currency: result.currency,
      category: result.category,
      subcategory: result.subcategory,
      location: result.location,
      created_at: result.created_at,
      updated_at: result.created_at,
      view_count: result.view_count,
      first_image: result.first_image,
      images: result.images,
      image_count: result.image_count,
      is_featured: result.is_featured,
      seller: result.seller,
      table_name: result.table_name,
      status: 'active',
      has_images: result.image_count > 0,
    };
  }

  getSimilarityColor(score: number): string {
    if (score >= 0.9) return '#10b981';
    if (score >= 0.8) return '#3b82f6';
    if (score >= 0.7) return '#f59e0b';
    return '#ef4444';
  }

  getSimilarityDescription(score: number): string {
    if (score >= 0.9) return 'Excellent match';
    if (score >= 0.8) return 'Good match';
    if (score >= 0.7) return 'Fair match';
    return 'Poor match';
  }

  formatSimilarityScore(score: number): string {
    return `${(score * 100).toFixed(0)}%`;
  }

  sortReverseSearchResults(): void {
    if (!this.isReverseImageSearch || this.reverseSearchResults.length === 0) {
      return;
    }

    const sortedResults = [...this.reverseSearchResults];

    switch (this.currentSort) {
      case 'similarity':
        sortedResults.sort((a, b) => b.similarity_score - a.similarity_score);
        break;

      case 'price_low':
        sortedResults.sort((a, b) => a.price - b.price);
        break;

      case 'price_high':
        sortedResults.sort((a, b) => b.price - a.price);
        break;

      case 'newest':
        sortedResults.sort((a, b) => {
          const dateA = new Date(a.created_at).getTime();
          const dateB = new Date(b.created_at).getTime();
          return dateB - dateA;
        });
        break;

      case 'oldest':
        sortedResults.sort((a, b) => {
          const dateA = new Date(a.created_at).getTime();
          const dateB = new Date(b.created_at).getTime();
          return dateA - dateB;
        });
        break;

      default:
        sortedResults.sort((a, b) => b.similarity_score - a.similarity_score);
    }

    this.reverseSearchResults = sortedResults;
  }

  goToPage(page: number): void {
    if (this.isReverseImageSearch) {
      return;
    }

    if (
      page < 1 ||
      (this.searchResult && page > this.searchResult.pagination.pages)
    ) {
      return;
    }
    this.updateUrl({ page });
  }

  updateUrl(queryParams: any): void {
    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: queryParams,
        queryParamsHandling: 'merge',
      })
      .then(() => {
        this.performSearch();
      });
  }

  retrySearch(): void {
    if (this.isReverseImageSearch) {
      this.router.navigate(['/']);
    } else {
      this.performSearch();
    }
  }

  private loadSubcategories(): void {
    if (this.currentCategory === 'all') {
      this.availableSubcategories = [];
      return;
    }

    this.isLoadingSubcategories = true;

    this.searchService
      .getSubcategories(this.currentCategory)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.availableSubcategories = response.subcategories;
          this.isLoadingSubcategories = false;
        },
        error: (error) => {
          console.error('Failed to load subcategories:', error);
          this.availableSubcategories = [];
          this.isLoadingSubcategories = false;
        },
      });
  }

  selectSubcategory(subcategoryKey: string): void {
    if (
      this.isChangingSubcategory ||
      this.currentSubcategory === subcategoryKey
    ) {
      return;
    }

    this.isChangingSubcategory = true;
    this.pendingSubcategory = subcategoryKey;
    this.resultsVisible = false;

    setTimeout(() => {
      if (this.currentKeyword && this.currentKeyword.trim()) {
        this.router.navigate(
          [
            '/search',
            this.currentCategory,
            subcategoryKey,
            encodeURIComponent(this.currentKeyword),
          ],
          {
            queryParams: {
              sort: this.currentSort,
              page: 1,
            },
            replaceUrl: false,
          }
        );
      } else {
        this.router.navigate(
          ['/search', this.currentCategory, subcategoryKey, 'browse'],
          {
            queryParams: {
              sort: this.currentSort,
              page: 1,
            },
            replaceUrl: false,
          }
        );
      }
    }, 300);
  }

  clearSubcategory(): void {
    if (this.isChangingSubcategory) {
      return;
    }

    this.isChangingSubcategory = true;
    this.pendingSubcategory = undefined;
    this.resultsVisible = false;

    setTimeout(() => {
      if (this.currentKeyword && this.currentKeyword.trim()) {
        this.router.navigate(
          [
            '/search',
            this.currentCategory,
            encodeURIComponent(this.currentKeyword),
          ],
          {
            queryParams: {
              sort: this.currentSort,
              page: 1,
            },
          }
        );
      } else {
        this.router.navigate(['/search', this.currentCategory], {
          queryParams: {
            sort: this.currentSort,
            page: 1,
          },
        });
      }
    }, 300);
  }

  viewListing(listing: LocalListing | LocalReverseResult): void {
    if (listing.isTouchScrolling || listing.touchMoved) {
      return;
    }

    if (listing.touchStartTime !== undefined) {
      const tapDuration = Date.now() - listing.touchStartTime;
      if (tapDuration > this.TAP_TIME_THRESHOLD) {
        return;
      }
    }

    const searchListing =
      'similarity_score' in listing
        ? this.convertReverseResultToSearchListing(listing)
        : listing;

    this.router.navigate([
      '/listing-details',
      searchListing.subcategory,
      searchListing.id,
    ]);
  }

  getCategoryDisplayName(category: string): string {
    return this.searchService.getCategoryDisplayName(category);
  }

  getSubcategoryDisplayName(subcategory: string): string {
    return this.searchService.getSubcategoryDisplayName(subcategory);
  }

  formatPrice(price: number, currency: string): string {
    return this.searchService.formatPrice(price, currency);
  }

  formatDate(dateString: string): string {
    return this.searchService.formatDate(dateString);
  }

  getDefaultImage(category: string): string {
    const defaultImages: { [key: string]: string } = {
      vehicles: '/assets/images/listing-placeholder/no-image-placeholder.png',
      'real-estate':
        '/assets/images/listing-placeholder/no-image-placeholder.png',
      marketplace:
        '/assets/images/listing-placeholder/no-image-placeholder.png',
      jobs: '/assets/images/listing-placeholder/no-image-placeholder.png',
    };
    return (
      defaultImages[category] ||
      '/assets/images/listing-placeholder/no-image-placeholder.png'
    );
  }

  isNewListing(createdAt: string): boolean {
    const created = new Date(createdAt);
    const now = new Date();
    const diffDays = Math.floor(
      (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)
    );
    return diffDays <= 3;
  }

  onImageError(event: any, listing: LocalListing | LocalReverseResult): void {
    event.target.src = this.getDefaultImage(listing.category);
  }

  trackByListingId(
    index: number,
    listing: LocalListing | LocalReverseResult
  ): number {
    return listing.id;
  }

  getVisiblePages(): (number | string)[] {
    if (!this.searchResult || this.isReverseImageSearch) return [];

    const currentPage = this.searchResult.pagination.page;
    const totalPages = this.searchResult.pagination.pages;
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 4) {
        pages.push('...');
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 3) {
        pages.push('...');
      }

      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  }

  hasActiveFilters(): boolean {
    return Object.keys(this.currentFilters).some((key) => {
      const value = this.currentFilters[key];
      return value !== undefined && value !== null && value !== '';
    });
  }

  getActiveFiltersCount(): number {
    return Object.keys(this.currentFilters).filter((key) => {
      const value = this.currentFilters[key];
      return value !== undefined && value !== null && value !== '';
    }).length;
  }

  onFiltersChange(filters: SearchFilters): void {
    this.currentFilters = filters;

    const queryParams: { [key: string]: any } = {
      ...filters,
      sort: this.currentSort,
      page: 1,
    };

    Object.keys(queryParams).forEach((key) => {
      if (
        queryParams[key] === undefined ||
        queryParams[key] === null ||
        queryParams[key] === ''
      ) {
        delete queryParams[key];
      }
    });

    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: queryParams,
        replaceUrl: false,
      })
      .then(() => {
        this.performSearch();
      });
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  shouldShowWishlistButton(
    listing: LocalListing | LocalReverseResult
  ): boolean {
    if (!this.authService.isLoggedIn()) {
      return false;
    }

    const currentUserId = this.authService.getCurrentUserId();
    const listingUserId =
      'seller' in listing && listing.seller?.id
        ? listing.seller.id
        : 'user_id' in listing
        ? listing.user_id
        : undefined;

    if (currentUserId && listingUserId) {
      return currentUserId.toString() !== listingUserId.toString();
    }

    return true;
  }
}
